/**
 * A revisão do dado pelo perito, pelo painel do documento (E203.F4, ADR-0146 §6–§9). Espelha
 * `apps/api/types/revisao_do_dado.py` e `apps/api/runtime/revisoes_do_dado.py`.
 *
 * - `POST /casos/:id/dados/revisoes`: o valor novo com fundamento (a leitura do crédito com a
 *   fonte, o resultado de uma linha, o grau e o fato de um risco, o instante da assinatura);
 * - `GET /casos/:id/dados/creditos`: os créditos lidos do comprovante em vigor, por arquivo e página.
 *
 * E203.F4.C (F-E203.F4-04, F-E203.F4-07): a revisão leva a versão do documento que o painel mostrava
 * (`avaliacao_id`) e o servidor recusa a do laudo já assinado e a do documento que mudou desde então;
 * o cadastro, o protocolo e o código declarado gravam pelo painel com fundamento, pelas rotas
 * `PUT /casos/:id/dados/{cadastro,protocolo,codigo/:doc}`, que registram o histórico.
 */
import axios, { type AxiosInstance } from 'axios';

import { classifyCaseError, type CaseRecordData, type CaseUpdateData } from './cases';
import type { ExameEletronicoRecordData } from './exame_eletronico';
import { classifyExameEletronicoError } from './exame_eletronico_erros';
import type {
  CodigoDeclaradoInput,
  ModalidadeInformada,
  ProtocoloAssinaturaCasoData,
} from './exame_eletronico_protocolo';

export type AlvoDaRevisao = 'credito' | 'linha' | 'risco' | 'instante' | 'modalidade';
/** O histórico registra também o cadastro, o protocolo e o código revistos pelo painel. */
export type AlvoDoHistorico = AlvoDaRevisao | 'cadastro' | 'protocolo' | 'codigo';
export type CampoDoCreditoLido =
  | 'banco'
  | 'agencia'
  | 'conta'
  | 'titular'
  | 'documento'
  | 'valor'
  | 'data';
export type ResultadoDaLinha = 'consistente' | 'inconclusivo' | 'divergente';
export type GrauDoRisco = 'alto' | 'medio' | 'baixo';

export const RESULTADOS_DA_LINHA: readonly ResultadoDaLinha[] = [
  'consistente',
  'inconclusivo',
  'divergente',
];
export const GRAUS_DO_RISCO: readonly GrauDoRisco[] = ['alto', 'medio', 'baixo'];

/**
 * E211.F2 (owner, 01/11): as colunas de resultado da linha do documento na custódia (PARTE, seção
 * 2) e na integridade (JUÍZO, seção 4). A chave da revisão é `custodia:<letra>:<coluna>`, e o
 * resultado é o mesmo da linha da identidade, que cada coluna imprime com a palavra dela.
 */
export type ColunaDaCustodia = 'integridade' | 'vinculo';
export const COLUNAS_DA_CUSTODIA: readonly ColunaDaCustodia[] = ['integridade', 'vinculo'];

/** A palavra impressa (sem acento, minúsculas) que já é um resultado que o perito dá. */
const RESULTADO_IMPRESSO: Record<ColunaDaCustodia, Record<string, ResultadoDaLinha>> = {
  integridade: {
    consistente: 'consistente',
    'nao verificavel': 'inconclusivo',
    divergente: 'divergente',
    inconsistente: 'divergente',
  },
  vinculo: {
    consistente: 'consistente',
    'consistente (carimbo do tempo)': 'consistente',
    'carimbo do tempo': 'consistente',
    'objeto do carimbo': 'consistente',
    inconclusivo: 'inconclusivo',
    'nao localizado': 'inconclusivo',
    'nao confirmado': 'divergente',
    'outra transacao': 'divergente',
  },
};

/**
 * O resultado que a coluna imprime, como o painel o abre; `''` quando a palavra impressa não é um
 * resultado do perito (NÃO RECEBIDO, PRESERVADA, HASH REGISTRADO): nada vem escolhido.
 */
export function resultadoImpresso(
  coluna: ColunaDaCustodia,
  impresso: string | null | undefined,
): ResultadoDaLinha | '' {
  return (impresso && RESULTADO_IMPRESSO[coluna][impresso]) || '';
}

/**
 * E211.F3 (owner, 01/11): o resultado que o perito dá a um requisito da matriz pública
 * (`matriz:<n>`) — os da régua pública, sem o não exigível. A contagem acompanha; a conclusão da
 * formalização não muda por aqui.
 */
export type ResultadoDoRequisito =
  | 'atendido'
  | 'nao_violada'
  | 'parcial'
  | 'pendente'
  | 'nao_demonstrado'
  | 'irregular';
export const RESULTADOS_DO_REQUISITO: readonly ResultadoDoRequisito[] = [
  'atendido',
  'nao_violada',
  'parcial',
  'pendente',
  'nao_demonstrado',
  'irregular',
];
/** E211.F3: a chave da revisão do eixo de geolocalização da página 1. */
export const CHAVE_DO_EIXO = 'eixo:geolocalizacao';

/** O resultado impresso no requisito ("nao demonstrado"), como o painel o abre. */
export function requisitoImpresso(impresso: string | null | undefined): ResultadoDoRequisito | '' {
  return vigenteEntre(RESULTADOS_DO_REQUISITO, impresso?.replaceAll(' ', '_'));
}

/**
 * E211.F4 (owner, 01/11): as colunas de valor livre — a natureza do endereço de rede
 * (`rede:<ip>:natureza`) e a localização e a distância da referência da geolocalização
 * (`geolocalizacao:<referência>:<coluna>`). A fonte vai em `texto`.
 */
export type ColunaDoValor = 'natureza' | 'localizacao' | 'distancia';

/** As colunas de valor livre da linha, com o valor impresso de cada uma; vazio fora delas. */
export function colunasDoValor(
  linha: string | null,
  valor: string | null | undefined,
  texto: string | null | undefined,
): { coluna: ColunaDoValor; impresso: string }[] {
  if (linha?.startsWith('rede:')) return [{ coluna: 'natureza', impresso: valor ?? '' }];
  if (linha?.startsWith('geolocalizacao:')) {
    return [
      { coluna: 'localizacao', impresso: valor ?? '' },
      { coluna: 'distancia', impresso: texto ?? '' },
    ];
  }
  return [];
}

/** `custodia:<linha>:<coluna>`, a chave da revisão da coluna da linha (`custodia:<sha256>` desde a
 * E216.F3; a de antes, `custodia:<letra>`). */
export function chaveDaCustodia(linha: string, coluna: ColunaDaCustodia): string {
  return `${linha}:${coluna}`;
}
export const FUNDAMENTO_MINIMO = 10;
export const CHAVE_DO_INSTANTE = 'assinatura_registrada_em';
/** E222.F1: a modalidade da operação que o perito informa quando o material não a identifica. */
export const CHAVE_DA_MODALIDADE = 'modalidade';
export const MODALIDADES_INFORMADAS: readonly ModalidadeInformada[] = [
  'consignado_inss',
  'consignado_trabalhador',
  'outra',
];

export interface PedidoDeRevisaoDoDadoData {
  alvo: AlvoDaRevisao;
  chave: string;
  valor: string;
  texto?: string | null;
  documento_id?: string | null;
  pagina?: number | null;
  fundamento: string;
  /** A versão do documento que o painel mostrava; sem ela, o servidor confere só a assinatura. */
  avaliacao_id?: string;
}

export interface RevisaoDoDadoData extends Required<
  Omit<PedidoDeRevisaoDoDadoData, 'alvo' | 'texto' | 'avaliacao_id'>
> {
  alvo: AlvoDoHistorico;
  id: string;
  case_id: string;
  texto: string | null;
  anterior: string | null;
  criada_por: string;
  criada_em: string;
}

export interface CreditoNoPainelData {
  documento_id: string;
  pagina: number;
  banco: string | null;
  agencia: string | null;
  conta: string | null;
  titular: string | null;
  documento: string | null;
  valor: string | null;
  data: string | null;
  /** O número do contrato lido naquele crédito. */
  contrato: string | null;
}

export interface CreditosDoCasoData {
  case_id: string;
  creditos: CreditoNoPainelData[];
}

/** A chave do campo do crédito: o arquivo do comprovante, a página do crédito e o campo. */
export function chaveDoCredito(c: CreditoNoPainelData, campo: CampoDoCreditoLido): string {
  return `${c.documento_id}:${c.pagina}:${campo}`;
}

/** O valor vigente que o documento imprime, se for uma das opções; senão, nenhuma escolha (`''`). */
export function vigenteEntre<T extends string>(
  opcoes: readonly T[],
  valor: string | null | undefined,
): T | '' {
  return opcoes.find((opcao) => opcao === valor) ?? '';
}

export type MotivoDaRecusa = 'laudo_ja_assinado' | 'edicao_desatualizada';

const RECUSAS: Record<string, MotivoDaRecusa> = {
  LAUDO_JA_ASSINADO: 'laudo_ja_assinado',
  EDICAO_DESATUALIZADA: 'edicao_desatualizada',
};

/** A revisão recusada pela versão: o laudo dela já foi assinado, ou o documento mudou. */
export class RevisaoDoDadoRecusada extends Error {
  public readonly motivo: MotivoDaRecusa;

  constructor(motivo: MotivoDaRecusa) {
    super(`revisao_do_dado_recusada:${motivo}`);
    this.name = 'RevisaoDoDadoRecusada';
    this.motivo = motivo;
  }
}

function recusaDaVersao(err: unknown): MotivoDaRecusa | null {
  if (!axios.isAxiosError(err) || err.response?.status !== 409) return null;
  const detail = (err.response.data as { detail?: unknown } | undefined)?.detail;
  const codigo =
    typeof detail === 'object' && detail !== null && 'code' in detail
      ? String((detail as { code: unknown }).code)
      : detail;
  return typeof codigo === 'string' ? (RECUSAS[codigo] ?? null) : null;
}

export async function registrarRevisaoDoDado(
  client: AxiosInstance,
  caseId: string,
  pedido: PedidoDeRevisaoDoDadoData,
): Promise<RevisaoDoDadoData> {
  try {
    const { data } = await client.post<RevisaoDoDadoData>(
      `/casos/${caseId}/dados/revisoes`,
      pedido,
    );
    return data;
  } catch (err) {
    const recusa = recusaDaVersao(err);
    throw recusa !== null ? new RevisaoDoDadoRecusada(recusa) : classifyExameEletronicoError(err);
  }
}

export async function lerCreditosDoCaso(
  client: AxiosInstance,
  caseId: string,
): Promise<CreditosDoCasoData> {
  try {
    const { data } = await client.get<CreditosDoCasoData>(`/casos/${caseId}/dados/creditos`);
    return data;
  } catch (err) {
    throw classifyExameEletronicoError(err);
  }
}

/** O cadastro do caso pelo painel: só o que mudou, com fundamento (recusas do `PATCH /casos/:id`). */
export async function salvarCadastroPeloPainel(
  client: AxiosInstance,
  caseId: string,
  mudancas: CaseUpdateData,
  fundamento: string,
): Promise<CaseRecordData> {
  try {
    const { data } = await client.put<CaseRecordData>(`/casos/${caseId}/dados/cadastro`, {
      mudancas,
      fundamento,
    });
    return data;
  } catch (err) {
    throw classifyCaseError(err);
  }
}

/** O protocolo do caso pelo painel, com fundamento (as recusas do protocolo do caso). */
export async function salvarProtocoloPeloPainel(
  client: AxiosInstance,
  caseId: string,
  protocolo: ProtocoloAssinaturaCasoData,
  fundamento: string,
): Promise<ProtocoloAssinaturaCasoData> {
  try {
    const { data } = await client.put<ProtocoloAssinaturaCasoData>(
      `/casos/${caseId}/dados/protocolo`,
      { protocolo, fundamento },
    );
    return data;
  } catch (err) {
    throw classifyExameEletronicoError(err);
  }
}

/** O código declarado de um documento pelo painel, com fundamento (as recusas do código da peça). */
export async function salvarCodigoPeloPainel(
  client: AxiosInstance,
  caseId: string,
  sourceDocumentId: string,
  codigo: CodigoDeclaradoInput,
  fundamento: string,
): Promise<ExameEletronicoRecordData> {
  try {
    const { data } = await client.put<ExameEletronicoRecordData>(
      `/casos/${caseId}/dados/codigo/${sourceDocumentId}`,
      { codigo, fundamento },
    );
    return data;
  } catch (err) {
    throw classifyExameEletronicoError(err);
  }
}
